// ShareLinkModal.js - Modal für Share Link Erstellung mit Einstellungen
import React, { useState, useEffect } from 'react';

function ShareLinkModal({ file, onClose, onCreateLink, token }) {
  const [expiresIn, setExpiresIn] = useState(7); // Tage
  const [maxDownloads, setMaxDownloads] = useState('unlimited');
  const [customDownloads, setCustomDownloads] = useState(10);
  const [passwordProtected, setPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [createdLink, setCreatedLink] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    
    try {
      const response = await fetch('/api/share/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_id: file.id,
          expires_in_days: expiresIn,
          max_downloads: maxDownloads === 'unlimited' ? null : (maxDownloads === 'custom' ? customDownloads : parseInt(maxDownloads)),
          password: passwordProtected ? password : null
        })
      });

      if (!response.ok) throw new Error('Failed to create share link');

      const data = await response.json();
      setCreatedLink(data);
      
      if (onCreateLink) {
        onCreateLink(data);
      }
    } catch (error) {
      console.error('Share link creation error:', error);
      // Toast would go here
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(createdLink.share_url);
      // Copied
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  const formatExpiryDate = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.88)',backdropFilter:'blur(8px)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}} onTouchMove={e => e.stopPropagation()}>
      <div style={{width:'100%',maxWidth:'500px',maxHeight:'calc(100vh - 6rem)',overflowY:'auto',WebkitOverflowScrolling:'touch',background:'#0f1923',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'16px',padding:'1.25rem'}}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-white mb-1">Share Link erstellen</h3>
              <p className="text-slate-400 text-sm truncate max-w-md">{file.filename}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!createdLink ? (
            /* Configuration */
            <div className="space-y-4">
              {/* Ablaufzeit */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Link gültig für
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {[1, 7, 30, 90].map((days) => (
                    <button
                      key={days}
                      onClick={() => setExpiresIn(days)}
                      className={`px-2 py-2 rounded-lg text-sm font-medium transition-all ${expiresIn === days ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                    >
                      {days === 1 ? '1 Tag' : `${days} Tage`}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Läuft ab am: {formatExpiryDate(expiresIn)}
                </p>
              </div>

              {/* Download Limit */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Maximale Downloads
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[{v:'unlimited',l:'\u221e'},{v:'1',l:'1x'},{v:'5',l:'5x'},{v:'10',l:'10x'},{v:'25',l:'25x'},{v:'50',l:'50x'},{v:'custom',l:'...'}].map(({v,l}) => (
                    <button key={v} onClick={() => setMaxDownloads(v)}
                      className={`px-3 py-2.5 rounded-xl font-medium text-sm transition-all ${maxDownloads === v ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                    >{l}</button>
                  ))}
                </div>
                {maxDownloads === 'custom' && (
                  <div className="mt-2">
                    <input type="number" min="1" max="1000" value={customDownloads}
                      onChange={(e) => setCustomDownloads(parseInt(e.target.value) || 1)}
                      className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-400/50 transition-all"
                      placeholder="Anzahl Downloads" />
                  </div>
                )}
              </div>

              {/* Password Protection */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Passwortschutz
                </label>
                
                {/* Password Toggle */}
                <button
                  onClick={() => {
                    setPasswordProtected(!passwordProtected);
                    if (passwordProtected) setPassword('');
                  }}
                  className={`w-full px-2 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-between ${passwordProtected ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/30' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                >
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Mit Passwort schützen
                  </span>
                  <div className={`w-12 h-6 rounded-full transition-all ${passwordProtected ? 'bg-white/20' : 'bg-white/5'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${passwordProtected ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`}></div>
                  </div>
                </button>

                {/* Password Input */}
                {passwordProtected && (
                  <div className="mt-3 space-y-2">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 transition-all"
                      placeholder="Passwort eingeben"
                      minLength={4}
                    />
                    <p className="text-xs text-slate-500">
                      🔒 Empfänger müssen dieses Passwort eingeben
                    </p>
                  </div>
                )}
              </div>

              {/* Summary */}
              <div className="bg-gradient-to-br from-cyan-500/10 to-blue-600/10 border border-cyan-400/20 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-white mb-2">Zusammenfassung</h4>
                <div className="space-y-1 text-sm text-slate-300">
                  <p>• Gültig bis: <span className="text-cyan-400">{formatExpiryDate(expiresIn)}</span></p>
                  <p>• Downloads: <span className="text-cyan-400">
                    {maxDownloads === 'unlimited' ? 'Unbegrenzt' : 
                     maxDownloads === 'custom' ? `${customDownloads}x` : 
                     `${maxDownloads}x`}
                  </span></p>
                  <p>• Passwort: <span className={passwordProtected ? "text-purple-400" : "text-slate-500"}>
                    {passwordProtected ? '🔒 Geschützt' : 'Kein Schutz'}
                  </span></p>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={handleCreate}
                disabled={creating || (passwordProtected && password.length < 4)}
                className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Erstelle Link...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Link erstellen
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </span>
                )}
              </button>
            </div>
          ) : (
            /* Created Link */
            <div className="space-y-4">
              {/* Success Icon */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                  <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full">
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <h4 className="text-2xl font-bold text-white mb-2">Link erstellt!</h4>
                <p className="text-slate-400 text-sm">Teile diesen Link mit anderen</p>
              </div>

              {/* Link Display */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-2">Share Link</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={createdLink.share_url}
                    readOnly
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg transition-all"
                    title="Kopieren"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Link Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-cyan-500/10 to-transparent border border-cyan-400/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-slate-400">Gültig bis</span>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {new Date(createdLink.expires_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-400/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="text-xs text-slate-400">Max Downloads</span>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {createdLink.max_downloads || '∞'}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={copyToClipboard}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all active:scale-95"
                >
                  Link kopieren
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all"
                >
                  Schließen
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

export default ShareLinkModal;
